import { storeToRefs } from 'pinia'
import { getEsgImagesByReportType } from '~/constants/esg'
import { BATTERY_EMISSIONS_ANALYSER } from '~/constants/services/constants'
import { MARKET_SLUG_BY_TYPE, MARKET_TYPES } from '~/src/constants/markets'
import type { TMarketSlug } from '~/src/types/markets'
import type { TReport, TReportCard } from '~/src/types/reports'
import { getReportCards } from '~/src/utils/reports/filter'
import { setReportImages } from '~/src/utils/reports/image'
import useAuthStore from '~/stores/auth'
import { getMarketForRoute } from '~/src/utils/markets-with-prices'

type TGetImagesByReportTypeParams = {
  report: TReport
  category: string
  type?: string
}

const getImagesByReportType = ({
  report,
  category,
  type = 'DEFAULT',
}: TGetImagesByReportTypeParams) => {
  try {
    return getEsgImagesByReportType(category, type)
  } catch {
    return report.images
  }
}

export const useReports = () => {
  const authStore = useAuthStore()
  const { jwtToken } = storeToRefs(authStore)
  const { $downloadManager } = useNuxtApp()

  const getBatteryEmissionsAnalyserReports = async (
    market: string,
    type = 'DEFAULT',
  ) => {
    return useFetch('/api/reports', {
      key: `battery-emissions-analyser-reports-${market}`,
      lazy: true,
      query: {
        market,
        'marketTypes[]': ['esg'],
        'tags[]': ['esg-battery-emissions-analyser'],
      },
      watch: [jwtToken],
      onRequest({ options }) {
        options.headers.set('Authorization', `Bearer ${jwtToken.value}`)
      },
      transform: ({ data: reports }) => {
        if (!reports) return []
        if (reports.length === 0) return []

        return [...reports].map((report) => {
          const images = getImagesByReportType({
            report,
            category: BATTERY_EMISSIONS_ANALYSER.category,
            type,
          })
          return { ...report, images }
        })
      },
    })
  }

  const getReportAndModelData = async (
    marketSlug: TMarketSlug,
    marketTypes: string[] = MARKET_TYPES,
    showTotal?: boolean,
    pageLimit: number = 10,
  ): Promise<TReportCard[]> => {
    const reports: TReport[] = await $downloadManager.getAllReports({
      market: marketSlug,
      marketTypes,
      page_limit: pageLimit,
      showTotal,
    })

    if (!reports || reports.length === 0) return []

    const reportsWithDataReportsLink = reports.map((report) => {
      const slug = MARKET_SLUG_BY_TYPE[report.market_type]
      const market = getMarketForRoute(marketSlug)
      return {
        ...report,
        link: `${market}/data-reports/${slug}/report/${report.id}`,
      }
    })

    const reportCards: TReportCard[] = getReportCards(
      reportsWithDataReportsLink,
      // If pageLimit is -1, we want to show all reports. Otherwise, we want to show one more than the pageLimit.
      // This applies to prices, market and weekly reports (briefings) only.
      pageLimit === -1 ? reports.length : pageLimit + 1,
    )
    return setReportImages(reportCards, marketSlug)
  }

  return {
    getReportAndModelData,
    getBatteryEmissionsAnalyserReports,
  }
}
